import { resolveCurrentUid } from '@/utils/auth'
import { oauthCallback } from '@/services/sheets'
import { takeGooglePkceVerifier } from '@/utils/googlePkce'
import { invalidateGoogleConnection } from '../hooks/googleConnectionCache'
import {
  finishGoogleReconnect,
  nonceFromGoogleReturn,
  readGoogleReconnectSession,
} from './googleReconnectSession'

const pending = new Map<string, Promise<void>>()

/** Exchange once, even when React remounts the callback. Never attach to another user. */
export async function completeGoogleReconnect(
  path: string,
  code: string | null,
  error: string | null
) {
  const nonce = nonceFromGoogleReturn(path)
  const session = readGoogleReconnectSession()
  const uid = await resolveCurrentUid()
  if (!nonce || !session || session.nonce !== nonce || session.uid !== uid) {
    throw new Error(
      'This Google connection belongs to a different or expired session. Return to Create New and try again.'
    )
  }
  if (session.outcome === 'connected') return
  if (error === 'access_denied') {
    finishGoogleReconnect(uid, nonce, 'cancelled')
    invalidateGoogleConnection(uid)
    return
  }
  if (!code || error) {
    finishGoogleReconnect(uid, nonce, 'failed')
    throw new Error('Google authorization did not finish. Please try again.')
  }
  const existing = pending.get(nonce)
  if (existing) return existing
  // One use: a remount shares `existing` above, and any later replay finds no
  // verifier. Starts made before PKCE shipped have none either.
  const codeVerifier = takeGooglePkceVerifier(nonce)
  if (!codeVerifier) {
    finishGoogleReconnect(uid, nonce, 'failed')
    throw new Error(
      'This Google authorization has expired. Return to Create New and reconnect Google.'
    )
  }
  const work = (async () => {
    try {
      await oauthCallback(code, codeVerifier, uid)
      if ((await resolveCurrentUid()) !== uid)
        throw new Error('Signed-in user changed. Please return to Create New.')
      finishGoogleReconnect(uid, nonce, 'connected')
      invalidateGoogleConnection(uid)
    } catch (cause) {
      finishGoogleReconnect(uid, nonce, 'failed')
      throw cause
    }
  })()
  pending.set(nonce, work)
  try {
    await work
  } finally {
    pending.delete(nonce)
  }
}
